// Pure mapping from a Model Studio cuboid document to a three.js Group, used
// by the Studio's GLB exporter and unit-testable without a renderer.
import { Group, Mesh, MeshStandardMaterial } from 'three';
import { evaluateModelCsg, hasCsgOps, type ModelCsgOp } from './modelCsg';
import { createUnitModelGeometry, type ModelProfile, type ModelShape } from './modelPrimitives';

/**
 * One authored primitive in Model Studio design units. `x`/`z` locate the
 * element's centre on the ground plane; `y` is its bottom face before
 * rotation (matching the studio's live preview, where an element sits on
 * the floor at y=0 and feet may dip below it). `w`/`h`/`d` scale the unit
 * geometry for `shape` (docs/3d-content-pipeline/roadmap.md 3.1 Phase 1) -
 * omitted `shape` defaults to the original box. `rotation` (degrees, XYZ
 * Euler order) turns the element in place around its own centre, applied
 * after the position offset below - it does not re-ground a rotated
 * element, the same "rotate about the object's own pivot" behaviour every
 * simple primitive/CAD editor this roadmap surveyed (Tinkercad, Roblox,
 * Spline) uses. `csgOp` (3.1 Phase 2) combines this element into the
 * running boolean result instead of standing alone - unset/'add' means
 * solid (union), matching every element's behaviour before CSG existed.
 */
export interface ModelGroupElement {
  name: string;
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  /** CSS hex colour, e.g. '#2142d4'. */
  colour: string;
  shape?: ModelShape;
  rotation?: [number, number, number];
  csgOp?: ModelCsgOp;
  /** Footprint polygon for `shape: 'extrude'` (3.1 Phase 3); ignored by
   * every other shape. */
  profile?: ModelProfile;
}

/** Design units per exported metre: a 100-unit cuboid is 1m. */
export const MODEL_UNITS_PER_METRE = 100;

const DEG_TO_RAD = Math.PI / 180;

/**
 * Build a Group of primitive meshes from authored elements, design units
 * converted to metres and the whole model grounded so its lowest unrotated
 * bottom face sits on y=0 (feet on the floor).
 *
 * When no element requests a real boolean op (the common case - every
 * project before CSG existed, and most elements after), this stays exactly
 * the original one-mesh-per-element bake: a plain union of non-overlapping
 * shapes renders identically either way, so there is no reason to pay for
 * an evaluator pass. Once any element sets a real `csgOp`, the whole model
 * bakes as one evaluated CSG mesh instead (see modelCsg.ts) - a boolean
 * result is one combined solid by definition, not a per-element list.
 */
export function buildModelGroup(elements: ModelGroupElement[]): Group {
  const group = new Group();
  group.name = 'model';
  if (elements.length === 0) return group;

  const floorY = Math.min(...elements.map((el) => el.y));
  const toMetres = (units: number) => units / MODEL_UNITS_PER_METRE;
  const grounded: ModelGroupElement[] = elements.map((el) => ({
    ...el,
    x: toMetres(el.x),
    y: toMetres(el.y - floorY),
    z: toMetres(el.z),
    w: toMetres(el.w),
    h: toMetres(el.h),
    d: toMetres(el.d),
  }));

  if (hasCsgOps(grounded)) {
    const result = evaluateModelCsg(grounded);
    result.name = 'model_csg';
    group.add(result);
    return group;
  }

  const materials = new Map<string, MeshStandardMaterial>();
  for (const el of grounded) {
    let material = materials.get(el.colour);
    if (!material) {
      // This loop only ever runs when `hasCsgOps(grounded)` is false (the
      // CSG evaluator path above already returned otherwise), which is
      // every project before CSG existed and most elements after - so it
      // has to keep three.js's own MeshStandardMaterial default
      // (roughness 1, metalness 0) exactly, not the roughness 0.7/
      // metalness 0.05 the new CSG bake path (modelCsg.ts) and the live
      // editor's preview mesh (editor-model.tsx) use. Changing the default
      // here would silently re-export every pre-existing Model Studio
      // document with a different material look on its next save, with no
      // migration or opt-in - only a document that actually engages a real
      // boolean op picks up the new look, via evaluateModelCsg.
      material = new MeshStandardMaterial({ color: el.colour });
      material.name = el.colour;
      materials.set(el.colour, material);
    }
    const mesh = new Mesh(createUnitModelGeometry(el.shape ?? 'box', el.profile), material);
    mesh.name = el.name;
    mesh.scale.set(el.w, el.h, el.d);
    mesh.position.set(el.x, el.y + el.h / 2, el.z);
    if (el.rotation) {
      mesh.rotation.set(el.rotation[0] * DEG_TO_RAD, el.rotation[1] * DEG_TO_RAD, el.rotation[2] * DEG_TO_RAD);
    }
    group.add(mesh);
  }
  return group;
}
